/** Watch folders: folders PRISM watches for new material, each with its own import settings. */
import { z } from 'zod';
import { WatchQcSchema } from './qc.ts';
import { RightsDatesSchema } from './rights.ts';

/** A watch folder's default "Import at most N files at a time" (1.0.0-rc.13). */
export const DEFAULT_MAX_CONCURRENT_IMPORTS = 20;

export const WatchFolderSettingsSchema = z.object({
  /** Generate a proxy, poster, scrub images and waveform on ingest. */
  proxy: z.boolean().default(true),
  /** Read metadata from an XML sidecar with the same name (clip.xml beside clip.mov). */
  sidecarXml: z.boolean().default(true),
  /** Read metadata from a JSON sidecar with the same name (clip.json beside clip.mov). */
  sidecarJson: z.boolean().default(true),
  /** Which wins when a sidecar and the folder's defaults both give a field: sidecar (default) or defaults. */
  sidecarPrecedence: z.enum(['sidecar', 'defaults']).default('sidecar'),
  /** Element (XML) or key path (JSON) → field mapping; empty matches names to fields by id or label. The target `_asset_id` names the asset (placeholder) the file belongs to. */
  /** 1.2.0: also the targets `_asset_class`, `_master_variant` and `_master_of` (sidecar.ts SIDECAR_RESERVED). */
  xmlMapping: z.array(z.object({ element: z.string().min(1).max(200), fieldId: z.string().regex(/^(f_[a-z0-9_]+|_asset_id|_asset_class|_master_variant|_master_of)$/) })).max(100).default([]),
  /** Keep other files with the same name (subtitles, documents, graphics) as attachments. */
  attachments: z.boolean().default(true),
  /** Also import .srt / .vtt / .ttml / .dfxp sidecars (1.9.0: and a .xml whose root is TTML) as subtitle tracks. */
  subtitleTracks: z.boolean().default(true),
  /** move: remove the files from the watch folder once they are verified in storage. copy: leave them. */
  afterIngest: z.enum(['move', 'copy']).default('move'),
  /** Content that is already in the library: catalogue it linked as a duplicate, or skip it. */
  duplicates: z.enum(['link', 'skip']).default('link'),
  /** The category every asset from this folder gets (Programme, Clip, …); none when absent. */
  categoryId: z.string().uuid().optional(),
  /**
   * 1.2.0: the master variant of what this folder ingests (its primary master), unless a sidecar names one
   * (sidecarPrecedence decides, as for metadata); absent: Programme. A sidecar with MasterOf attaches the file as a
   * further master of that asset instead.
   */
  masterVariantId: z.string().uuid().optional(),
  /** Metadata applied to every asset from this folder (sidecars win unless sidecarPrecedence is defaults). */
  defaults: z.record(z.string().regex(/^f_[a-z0-9_]+$/), z.unknown()).default({}),
  /** How long files must stay unchanged before they are picked up. */
  stabilitySeconds: z.number().int().min(1).max(3600).default(10),
  includeSubfolders: z.boolean().default(true),
  /**
   * Live ingest: catalogue a file while it is still being written and show a
   * live proxy, instead of waiting until it stops changing. For capture
   * systems that write growing MXF, MPEG-TS or fragmented MP4.
   */
  live: z.boolean().default(false),
  /** A live recording ends when its file has not grown for this long. */
  liveCloseSeconds: z.number().int().min(5).max(3600).default(30),
  /**
   * Agent watch folders: where the probe and proxy are made, on the storage
   * agent from the file still in the watch folder, or centrally. Poster, scrub
   * images and waveform are always made centrally from the proxy. Originals
   * stored on the same agent are always processed on the agent.
   */
  processOn: z.enum(['agent', 'central']).default('agent'),
  /**
   * Automated QC of what this folder ingests (1.0.0-rc.8): off unless enabled, so folders from earlier releases are
   * unchanged; without a profile the workspace's default profile is used.
   */
  qc: WatchQcSchema.optional(),
  /**
   * Import at most this many files at a time (1.0.0-rc.13): a file is in flight from importing until its ingest (and
   * its QC, when the folder runs QC) has finished or failed. Files beyond it wait in arrival order, oldest first, and
   * start as slots free up; nothing is skipped. Growing files of a live folder start recording at once, whatever it is.
   */
  maxConcurrentImports: z.number().int().min(1).max(500).default(DEFAULT_MAX_CONCURRENT_IMPORTS),
  /**
   * 1.8.0: Available from and until relative to arrival (rights.ts resolveRightsDates), resolved into the asset's own
   * values when the file is registered; the other rights values are ordinary `defaults`. Absent: none.
   */
  rightsDates: RightsDatesSchema.optional(),
});
export type WatchFolderSettings = z.infer<typeof WatchFolderSettingsSchema>;

export interface WatchFolderDTO {
  id: string;
  name: string;
  enabled: boolean;
  kind: 'server' | 'agent';
  /** server: path under the mounts root. */
  path: string | null;
  /** agent: which agent, and the folder under its watch root (or a named root from earlier releases). */
  agent: { id: string; name: string; site: string; root: string | null; path: string | null; connected: boolean; ffmpeg: string | null } | null;
  destination: { id: string; name: string } | null;
  settings: WatchFolderSettings;
  lastScanAt: string | null;
  lastError: string | null;
  counts: {
    ingested: number; duplicate: number; failed: number; importing: number; inFolder: number | null;
    /** In flight now (importing, or ingesting and QC not finished) and waiting for a slot (1.0.0-rc.13). */
    inFlight?: number; waiting?: number;
  };
  createdAt: string;
}

export interface WatchFolderItemDTO {
  id: string;
  path: string;
  sizeBytes: number;
  /** waiting (1.0.0-rc.13): stable and recorded, waiting for an import slot (the folder's maxConcurrentImports). */
  status: 'importing' | 'ingested' | 'duplicate' | 'failed' | 'waiting';
  asset: { id: string; title: string } | null;
  sidecars: Array<{ name: string; role: 'metadata' | 'attachment' | 'subtitles'; outcome: string }>;
  notes: string[];
  error: string | null;
  updatedAt: string;
  /** false for earlier imports of a path that was imported again (kept as history). */
  current: boolean;
  /** The file has left the folder since (moved away after import, or removed). */
  gone: boolean;
}

/** Split a folder's files into media with their same-named sidecars. */
export function groupWatchFiles<T extends { key: string }>(files: T[], isMedia: (name: string) => boolean): Array<{ media: T; sidecars: T[] }> {
  const byDir = new Map<string, T[]>();
  for (const f of files) {
    const dir = f.key.includes('/') ? f.key.slice(0, f.key.lastIndexOf('/')) : '';
    (byDir.get(dir) ?? byDir.set(dir, []).get(dir)!).push(f);
  }
  const groups: Array<{ media: T; sidecars: T[] }> = [];
  const video = /\.(mp4|mov|m4v|mxf|mkv|webm|avi|mts|m2ts|ts|mpg|mpeg|wmv|flv|3gp|dv|y4m|wav|mp3|aac|m4a|flac|aif|aiff|ogg|opus|bwf)$/i;
  for (const list of byDir.values()) {
    const name = (f: T) => f.key.slice(f.key.lastIndexOf('/') + 1);
    const stem = (f: T) => name(f).replace(/\.[^.]+$/, '');
    const media = list.filter((f) => isMedia(name(f)));
    // A still image that shares its name with a video or audio file is a sidecar (poster, artwork), not media.
    const primaries = media.filter((m) => video.test(name(m)) || !media.some((o) => o !== m && video.test(name(o)) && stem(o) === stem(m)));
    const claimed = new Set<T>();
    // Longest stems first so "clip.v2.mov" claims "clip.v2.srt" before "clip.mov" could.
    for (const m of [...primaries].sort((a, b) => stem(b).length - stem(a).length)) {
      const s = stem(m).toLowerCase();
      const sidecars = list.filter((f) => f !== m && !primaries.includes(f) && !claimed.has(f) && (name(f).toLowerCase().startsWith(`${s}.`)));
      sidecars.forEach((f) => claimed.add(f));
      groups.push({ media: m, sidecars });
    }
  }
  return groups.sort((a, b) => a.media.key.localeCompare(b.media.key));
}
